import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import {
  archiveAdminActionLog,
  fetchAdminActionLogs,
  fetchAdminActionTypes,
} from "../../../services/AdminHistoryService";
import "./AdminHistory.css";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

const SORT_OPTIONS = [
  { value: "date_desc", labelKey: "adminHistorySortDateDesc" },
  { value: "date_asc", labelKey: "adminHistorySortDateAsc" },
  { value: "name_asc", labelKey: "adminHistorySortNameAsc" },
  { value: "name_desc", labelKey: "adminHistorySortNameDesc" },
  { value: "email_asc", labelKey: "adminHistorySortEmailAsc" },
  { value: "email_desc", labelKey: "adminHistorySortEmailDesc" },
  { value: "action_asc", labelKey: "adminHistorySortActionAsc" },
  { value: "action_desc", labelKey: "adminHistorySortActionDesc" },
];

const ACTION_LABEL_KEYS = {
  "reservation.create": "adminHistoryAction_reservation_create",
  "reservation.update": "adminHistoryAction_reservation_update",
  "reservation.archive": "adminHistoryAction_reservation_archive",
  "reservation.restore": "adminHistoryAction_reservation_restore",
  "reservation.table.create": "adminHistoryAction_table_create",
  "reservation.table.update": "adminHistoryAction_table_update",
  "reservation.table.delete": "adminHistoryAction_table_delete",
  "support.create": "adminHistoryAction_support_create",
  "support.update": "adminHistoryAction_support_update",
  "support.archive": "adminHistoryAction_support_archive",
  "support.restore": "adminHistoryAction_support_restore",
  "support.reply": "adminHistoryAction_support_reply",
  "support.delete": "adminHistoryAction_support_delete",
  "claim.status_update": "adminHistoryAction_claim_status",
  "claim.archive": "adminHistoryAction_claim_archive",
  "claim.restore": "adminHistoryAction_claim_restore",
  "claim.delete": "adminHistoryAction_claim_delete",
  "lost_found.create": "adminHistoryAction_lost_found_create",
  "lost_found.update": "adminHistoryAction_lost_found_update",
  "lost_found.archive": "adminHistoryAction_lost_found_archive",
  "lost_found.restore": "adminHistoryAction_lost_found_restore",
  "lost_found.delete": "adminHistoryAction_lost_found_delete",
  "event.create": "adminHistoryAction_event_create",
  "event.update": "adminHistoryAction_event_update",
  "event.archive": "adminHistoryAction_event_archive",
  "event.restore": "adminHistoryAction_event_restore",
  "event.republish": "adminHistoryAction_event_republish",
  "event.delete": "adminHistoryAction_event_delete",
  "event_reservation.status_update": "adminHistoryAction_event_reservation_status",
  "event_reservation.archive": "adminHistoryAction_event_reservation_archive",
  "event_reservation.restore": "adminHistoryAction_event_reservation_restore",
  "event_reservation.delete": "adminHistoryAction_event_reservation_delete",
  "client.archive": "adminHistoryAction_client_archive",
  "client.restore": "adminHistoryAction_client_restore",
  "client.delete": "adminHistoryAction_client_delete",
  "client.contact_gmail": "adminHistoryAction_client_contact_gmail",
  "client.contact_whatsapp": "adminHistoryAction_client_contact_whatsapp",
  "reservation.contact_gmail": "adminHistoryAction_reservation_contact_gmail",
  "reservation.contact_whatsapp": "adminHistoryAction_reservation_contact_whatsapp",
  "plan_floor_setup.create": "adminHistoryAction_plan_floor_setup_create",
  "plan_floor_setup.update": "adminHistoryAction_plan_floor_setup_update",
  "plan_floor_setup.archive": "adminHistoryAction_plan_floor_setup_archive",
  "plan_floor_setup.restore": "adminHistoryAction_plan_floor_setup_restore",
  "plan_floor_setup.delete": "adminHistoryAction_plan_floor_setup_delete",
  "menu.create": "adminHistoryAction_menu_create",
  "menu.update": "adminHistoryAction_menu_update",
  "menu.archive": "adminHistoryAction_menu_archive",
  "menu.restore": "adminHistoryAction_menu_restore",
  "menu.delete": "adminHistoryAction_menu_delete",
  "category.create": "adminHistoryAction_category_create",
  "category.update": "adminHistoryAction_category_update",
  "category.archive": "adminHistoryAction_category_archive",
  "category.restore": "adminHistoryAction_category_restore",
  "category.delete": "adminHistoryAction_category_delete",
  "category.display_orientation": "adminHistoryAction_category_display_orientation",
  "category.bulkDisplayOrientation": "adminHistoryAction_category_display_orientation",
  "category.bulk_display_orientation": "adminHistoryAction_category_display_orientation",
  "subcategory.create": "adminHistoryAction_subcategory_create",
  "subcategory.update": "adminHistoryAction_subcategory_update",
  "subcategory.archive": "adminHistoryAction_subcategory_archive",
  "subcategory.restore": "adminHistoryAction_subcategory_restore",
  "subcategory.delete": "adminHistoryAction_subcategory_delete",
  "subcategory.display_orientation": "adminHistoryAction_subcategory_update",
  "subcategory.bulkDisplayOrientation": "adminHistoryAction_subcategory_update",
  "subcategory.bulk_display_orientation": "adminHistoryAction_subcategory_update",
};

const ACTION_TONE = {
  "reservation.create": "create",
  "reservation.update": "update",
  "reservation.archive": "archive",
  "reservation.restore": "restore",
  "reservation.table.create": "create",
  "reservation.table.update": "update",
  "reservation.table.delete": "delete",
  "support.create": "create",
  "support.update": "update",
  "support.archive": "archive",
  "support.restore": "restore",
  "support.reply": "update",
  "support.delete": "delete",
  "claim.status_update": "update",
  "claim.archive": "archive",
  "claim.restore": "restore",
  "claim.delete": "delete",
  "lost_found.create": "create",
  "lost_found.update": "update",
  "lost_found.archive": "archive",
  "lost_found.restore": "restore",
  "lost_found.delete": "delete",
  "event.create": "create",
  "event.update": "update",
  "event.archive": "archive",
  "event.restore": "restore",
  "event.republish": "restore",
  "event.delete": "delete",
  "event_reservation.status_update": "update",
  "event_reservation.archive": "archive",
  "event_reservation.restore": "restore",
  "event_reservation.delete": "delete",
  "client.archive": "archive",
  "client.restore": "restore",
  "client.delete": "delete",
  "client.contact_gmail": "update",
  "client.contact_whatsapp": "update",
  "reservation.contact_gmail": "update",
  "reservation.contact_whatsapp": "update",
  "plan_floor_setup.create": "create",
  "plan_floor_setup.update": "update",
  "plan_floor_setup.archive": "archive",
  "plan_floor_setup.restore": "restore",
  "plan_floor_setup.delete": "delete",
  "menu.create": "create",
  "menu.update": "update",
  "menu.archive": "archive",
  "menu.restore": "restore",
  "menu.delete": "delete",
  "category.create": "create",
  "category.update": "update",
  "category.archive": "archive",
  "category.restore": "restore",
  "category.delete": "delete",
  "category.display_orientation": "update",
  "category.bulkDisplayOrientation": "update",
  "category.bulk_display_orientation": "update",
  "subcategory.create": "create",
  "subcategory.update": "update",
  "subcategory.archive": "archive",
  "subcategory.restore": "restore",
  "subcategory.delete": "delete",
  "subcategory.display_orientation": "update",
  "subcategory.bulkDisplayOrientation": "update",
  "subcategory.bulk_display_orientation": "update",
};

const getInitials = (name = "", email = "") => {
  const source = String(name || "").trim() || String(email || "").trim();
  if (!source) return "?";
  if (source.includes("@")) {
    const local = source.split("@")[0];
    return local.slice(0, 2).toUpperCase();
  }
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const formatDateTime = (value) => {
  if (!value) return { date: "—", time: "—" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: "—", time: "—" };
  return {
    date: date.toLocaleDateString([], {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    }),
    time: date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
  };
};

const getVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  const pages = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  if (start > 2) pages.push("start-ellipsis");
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < totalPages - 1) pages.push("end-ellipsis");
  pages.push(totalPages);
  return pages;
};

const AdminHistory = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [archivingId, setArchivingId] = useState("");

  const hasActiveFilters = Boolean(search || actionFilter || dateFrom || dateTo);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminActionLogs({
        page,
        limit: pageSize,
        search,
        action: actionFilter,
        dateFrom,
        dateTo,
        sortBy,
      });
      setLogs(Array.isArray(data?.logs) ? data.logs : []);
      setTotal(Number(data?.total) || 0);
      setTotalPages(Math.max(1, Number(data?.totalPages) || 1));
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || t("adminHistoryLoadError"));
      setLogs([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, actionFilter, dateFrom, dateTo, sortBy, t]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    fetchAdminActionTypes()
      .then((data) => setActionTypes(Array.isArray(data?.actions) ? data.actions : []))
      .catch(() => setActionTypes([]));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, actionFilter, dateFrom, dateTo, sortBy, pageSize]);

  const currentPage = Math.min(page, totalPages);
  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const showingFrom = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo = Math.min(currentPage * pageSize, total);

  const getActionLabel = (action) => {
    const key = ACTION_LABEL_KEYS[action];
    if (key) {
      const translated = t(key);
      if (translated && translated !== key) return translated;
    }
    if (!action) return "—";
    return String(action)
      .split(".")
      .filter(Boolean)
      .map((part) =>
        part
          .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
          .replace(/[_-]+/g, " ")
          .trim()
          .split(/\s+/)
          .filter(Boolean)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ")
      )
      .join(" ");
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setActionFilter("");
    setDateFrom("");
    setDateTo("");
    setSortBy("date_desc");
  };

  const handleArchive = async (log) => {
    if (!log?._id || archivingId) return;

    const result = await Swal.fire({
      title: t("adminHistoryArchiveConfirm"),
      text: t("adminHistoryArchiveConfirmText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("adminHistoryArchiveAction"),
      cancelButtonText: t("cancel"),
      confirmButtonColor: "#fa8072",
      cancelButtonColor: "#94a3b8",
    });

    if (!result.isConfirmed) return;

    setArchivingId(log._id);
    try {
      await archiveAdminActionLog(log._id);
      toast.success(t("adminHistoryArchived"));
      await loadLogs();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || t("adminHistoryArchiveError")
      );
    } finally {
      setArchivingId("");
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu={t("navAdminHistory")} motherMenu={t("adminHistoryMother")} />

      <div className="admin-history-page">
        <section className="admin-history-panel">
          <div className="admin-history-panel__head">
            <div>
              <h2>{t("adminHistoryTitle")}</h2>
              <p>{t("adminHistoryHint")}</p>
            </div>
            <button
              type="button"
              className="admin-history-refresh"
              disabled={loading}
              onClick={loadLogs}
            >
              <i className={`fa fa-sync-alt${loading ? " fa-spin" : ""}`} aria-hidden="true" />
              {loading ? t("loading") : t("refresh")}
            </button>
          </div>

          <form className="admin-history-filters" onSubmit={handleSearchSubmit}>
            <label className="admin-history-field admin-history-field--search">
              <span className="admin-history-field__label">{t("adminHistorySearch")}</span>
              <span className="admin-history-search">
                <i className="fa fa-search" aria-hidden="true" />
                <input
                  type="search"
                  className="form-control"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder={t("adminHistorySearchPlaceholder")}
                  aria-label={t("adminHistorySearchPlaceholder")}
                />
              </span>
            </label>

            <label className="admin-history-field">
              <span className="admin-history-field__label">{t("adminHistoryFilterAction")}</span>
              <select
                className="admin-history-select"
                value={actionFilter}
                onChange={(event) => setActionFilter(event.target.value)}
                aria-label={t("adminHistoryFilterAction")}
              >
                <option value="">{t("adminHistoryFilterAllActions")}</option>
                {actionTypes.map((action) => (
                  <option key={action} value={action}>
                    {getActionLabel(action)}
                  </option>
                ))}
              </select>
            </label>

            <label className="admin-history-field">
              <span className="admin-history-field__label">{t("adminHistoryDateFrom")}</span>
              <input
                type="date"
                className="form-control admin-history-control"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(event) => setDateFrom(event.target.value)}
                aria-label={t("adminHistoryDateFrom")}
              />
            </label>

            <label className="admin-history-field">
              <span className="admin-history-field__label">{t("adminHistoryDateTo")}</span>
              <input
                type="date"
                className="form-control admin-history-control"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(event) => setDateTo(event.target.value)}
                aria-label={t("adminHistoryDateTo")}
              />
            </label>

            <label className="admin-history-field">
              <span className="admin-history-field__label">{t("adminHistorySortBy")}</span>
              <select
                className="admin-history-select"
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                aria-label={t("adminHistorySortBy")}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </label>

            <div className="admin-history-field admin-history-field--actions">
              <span className="admin-history-field__label" aria-hidden="true">
                &nbsp;
              </span>
              <div className="admin-history-field__actions">
                {hasActiveFilters || sortBy !== "date_desc" ? (
                  <button
                    type="button"
                    className="admin-history-clear"
                    onClick={handleClearFilters}
                  >
                    {t("reservationArchivedClearFilters")}
                  </button>
                ) : null}
                <button type="submit" className="admin-history-search-btn">
                  {t("adminHistorySearch")}
                </button>
              </div>
            </div>
          </form>

          {loading ? (
            <div className="admin-history-empty">
              <i className="fa fa-spinner fa-spin" aria-hidden="true" />
              <strong>{t("loading")}…</strong>
            </div>
          ) : logs.length === 0 ? (
            <div className="admin-history-empty">
              <i className="fa fa-history" aria-hidden="true" />
              <strong>{t("adminHistoryEmpty")}</strong>
              <p>{t("adminHistoryEmptyHint")}</p>
            </div>
          ) : (
            <div className="admin-history-table-wrap">
              <table className="admin-history-table">
                <thead>
                  <tr>
                    <th>{t("adminHistoryColAdmin")}</th>
                    <th>{t("adminHistoryColAction")}</th>
                    <th>{t("adminHistoryColDateTime")}</th>
                    <th>{t("adminHistoryColDescription")}</th>
                    <th className="admin-history-actions-head">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const when = formatDateTime(log.createdAt);
                    const tone = ACTION_TONE[log.action] || "default";
                    const busy = archivingId === log._id;
                    return (
                      <tr key={log._id}>
                        <td>
                          <div className="admin-history-user">
                            <span className="admin-history-avatar" aria-hidden="true">
                              {getInitials(log.userName, log.userEmail)}
                            </span>
                            <div className="admin-history-user__text">
                              <strong>{log.userName || "—"}</strong>
                              <span>{log.userEmail || "—"}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`admin-history-action is-${tone}`}>
                            {getActionLabel(log.action)}
                          </span>
                        </td>
                        <td>
                          <div className="admin-history-when">
                            <strong>{when.date}</strong>
                            <span>
                              <i className="fa fa-clock-o" aria-hidden="true" />
                              {when.time}
                            </span>
                          </div>
                        </td>
                        <td>
                          <p className="admin-history-description">
                            {log.description || "—"}
                          </p>
                        </td>
                        <td className="admin-history-actions-cell">
                          <div className="admin-history-row-actions">
                            <button
                              type="button"
                              className="admin-history-icon-btn is-archive"
                              disabled={busy}
                              title={t("adminHistoryArchiveAction")}
                              aria-label={t("adminHistoryArchiveAction")}
                              onClick={() => handleArchive(log)}
                            >
                              <i className="fa fa-archive" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="admin-history-pagination">
                <div className="admin-history-pagination__meta">
                  <span>
                    {t("reservationArchivedShowingCount", {
                      from: showingFrom,
                      to: showingTo,
                      total,
                    })}
                  </span>
                  <label className="admin-history-page-size">
                    <span>{t("adminHistoryPerPage")}</span>
                    <select
                      value={pageSize}
                      onChange={(event) => setPageSize(Number(event.target.value))}
                      aria-label={t("adminHistoryPerPage")}
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {totalPages > 1 ? (
                  <div className="admin-history-pagination__btns">
                    <button
                      type="button"
                      className="admin-history-page-btn"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      aria-label={t("previous")}
                    >
                      <i className="fa fa-chevron-left" aria-hidden="true" />
                    </button>
                    {visiblePages.map((pageNumber) =>
                      typeof pageNumber === "string" ? (
                        <span key={pageNumber} className="admin-history-page-ellipsis">
                          …
                        </span>
                      ) : (
                        <button
                          key={pageNumber}
                          type="button"
                          className={`admin-history-page-btn${
                            currentPage === pageNumber ? " is-active" : ""
                          }`}
                          onClick={() => setPage(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      className="admin-history-page-btn"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                      aria-label={t("next")}
                    >
                      <i className="fa fa-chevron-right" aria-hidden="true" />
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </section>
      </div>
    </Fragment>
  );
};

export default AdminHistory;
